import React from 'react';
import { Modal, Typography } from '@crisellevaldez/speculo-ui';
import { Alert } from '../alerts/Alert';

interface LoadingDialogProps {
  isOpen: boolean;
  message?: string;
  /** Progress percentage (0-100) */
  progress?: number;
  /** Show warning message about not leaving the page */
  showWarning?: boolean;
  /** Custom warning message */
  warningMessage?: string;
  /** Optional data-tour attribute for guided tours */
  'data-tour'?: string;
}

export const LoadingDialog: React.FC<LoadingDialogProps> = ({
  isOpen,
  message = 'Loading...',
  progress,
  showWarning = false,
  warningMessage = 'Please do not close or refresh this page until the process is complete.',
  'data-tour': dataTour,
}) => {
  const showProgress = progress !== undefined;

  return (
    <Modal isOpen={isOpen} onClose={() => {}}>
      <div
        className="flex flex-col items-center justify-center p-8 min-h-[280px]"
        {...(dataTour ? { 'data-tour': dataTour } : {})}
      >
        {/* Loading Spinner */}
        <div className="relative mb-4">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
          {showProgress && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{Math.round(progress)}%</span>
            </div>
          )}
        </div>

        {/* Message */}
        <Typography variant="h5" className="text-gray-700 dark:text-gray-300 text-center mb-2">
          {message}
        </Typography>

        {/* Progress Bar */}
        {showProgress && (
          <div className="w-full max-w-xs mx-auto mt-4">
            <div className="h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Warning Message */}
        {showWarning && (
          <div className="mt-6 max-w-sm">
            <Alert variant="warning" showIcon={false}>
              {warningMessage}
            </Alert>
          </div>
        )}
      </div>
    </Modal>
  );
};
